import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { User, Mail, Lock, CheckCircle2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(form.name, form.email, form.password);
      toast.success(`Welcome, ${user.name.split(" ")[0]}. Your shelf awaits.`);
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-book py-10 md:py-16 grid lg:grid-cols-2 gap-10 items-center min-h-[70vh]">
      <div className="hidden lg:block">
        <p className="text-primary font-semibold text-sm mb-2">Create your account</p>
        <h1 className="font-heading font-bold text-4xl xl:text-5xl text-slate-900 leading-tight">
          Join the shelf of every <span className="text-primary">American reader</span>.
        </h1>
        <p className="mt-4 text-lg text-slate-600 max-w-lg">
          One account for orders, tracking, wishlists, complaint tickets, and personalized recommendations.
        </p>
        <ul className="mt-8 space-y-3 max-w-md">
          {[
            "Track every order end-to-end",
            "Save books to your wishlist",
            "Manage returns & refunds in one click",
            "Faster checkout on future orders",
          ].map((b) => (
            <li key={b} className="flex items-center gap-3 text-slate-800">
              <CheckCircle2 size={18} className="text-primary shrink-0" />
              <span className="font-medium">{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-lift p-6 md:p-8">
          <h2 className="font-heading font-bold text-2xl text-slate-900">Create an account</h2>
          <p className="text-sm text-slate-500 mt-1">Takes less than a minute.</p>
          <form onSubmit={submit} data-testid="register-form" className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5 block">Full name</label>
              <div className="relative">
                <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className="pl-10 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5 block">Email address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="pl-10 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5 block">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="pl-10 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
            </div>
            <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-primary hover:bg-secondary text-white font-semibold text-base">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-slate-600 text-center">
            Already a member?{" "}
            <Link to="/login" className="font-semibold text-primary hover:text-secondary" data-testid="register-to-login">
              Sign in →
            </Link>
          </p>
        </div>
        <p className="mt-5 text-center text-xs text-slate-500">
          By creating an account, you agree to our <Link to="/terms" className="underline hover:text-primary">Terms</Link> and <Link to="/privacy" className="underline hover:text-primary">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
